import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  Container,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import formatDateLLL from '@/util/formatDateLLL';
import { useGpuClasses } from '@/providers';

import { fetchArchivedJobs } from '../../../actions/archivedJobs';

function ArchivedJobs() {
  const dispatch = useDispatch();
  const gpuClasses = useGpuClasses();
  const [sortBy, setSortBy] = useState('name');
  const [displayOrder, setDisplayOrder] = useState(true);
  const [displayCount, setDisplayCount] = useState(20);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [lastKeyUuid, setLastKeyUuid] = useState(undefined);
  const [lastKeySort, setLastKeySort] = useState(undefined);
  const [rowCount, setRowCount] = useState(0);

  const loadJobs = useCallback(
    async (page = 0) => {
      setLoading(true);
      try {
        const result = await fetchArchivedJobs(
          sortBy,
          displayCount,
          displayOrder,
          page > 0 ? lastKeyUuid : undefined,
          page > 0 ? lastKeySort : undefined
        );

        setRows(result.jobs);
        setLastKeyUuid(result.last_key_uuid);
        setLastKeySort(result.last_key_sort);
        setRowCount(
          result.jobs.length < displayCount
            ? page * displayCount + result.jobs.length
            : (page + 1) * displayCount + 1
        );
      } catch (error) {
        console.error('Error loading jobs:', error);
      } finally {
        setLoading(false);
      }
    },
    [displayCount, displayOrder, lastKeySort, lastKeyUuid, sortBy]
  );

  useEffect(() => {
    const initializeData = async () => {
      try {
        // GpuClasses are fetched by Layout via context
        await loadJobs();
      } catch (error) {
        console.error('Error initializing data:', error);
      }
    };

    initializeData();
  }, [dispatch, loadJobs]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    switch (name) {
      case 'sortBy':
        setSortBy(value);
        break;
      case 'displayOrder':
        setDisplayOrder(value);
        break;
      case 'displayCount':
        setDisplayCount(value);
        break;
      default:
        break;
    }
  };

  const handleSubmit = async () => {
    setLastKeyUuid(undefined);
    setLastKeySort(undefined);
    await loadJobs(0);
  };

  const getGpuClassName = (gpu_type_id) => {
    if (!gpu_type_id || !gpuClasses) return 'N/A';

    const gpu_class = gpuClasses.find(
      (gpu_class) => gpu_class.id === gpu_type_id
    );
    return gpu_class ? gpu_class.name : 'N/A';
  };

  const renderNoJobs = () => {
    return (
      <Alert severity='info'>
        <Typography variant='h6'>You have no archived jobs</Typography>
      </Alert>
    );
  };

  const sortByOptions = [
    { value: 'name', label: 'Name' },
    { value: 'start', label: 'Start time' },
    { value: 'end', label: 'End time' },
  ];

  const displayOrderOptions = [
    { value: true, label: 'Ascending' },
    { value: false, label: 'Descending' },
  ];

  const displayCountOptions = [
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
  ];

  const columns = [
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      valueGetter: (value, row) =>
        row.type.replace(/\b(\w)/g, (s) => s.toUpperCase()),
    },
    {
      field: 'gpu_type',
      headerName: 'GPU Type',
      flex: 1,
      valueGetter: (value, row) =>
        getGpuClassName(row.resources?.gpu_type_id),
    },
    {
      field: 'gpu_count',
      headerName: 'GPU Count',
      flex: 1,
      valueGetter: (value, row) => row.resources?.gpu_count || 0,
    },
    { field: 'credits', headerName: 'Credits', flex: 1 },
    {
      field: 'start',
      headerName: 'Start Time',
      flex: 1,
      valueGetter: (value, row) => formatDateLLL(row.start),
    },
    {
      field: 'end',
      headerName: 'End Time',
      flex: 1,
      valueGetter: (value, row) => formatDateLLL(row.end),
    },
  ];
  console.log(gpuClasses);
  return (
    <Container>
          <Box sx={{ py: 4 }}>
            <Typography variant='h4' component='h1' gutterBottom>
              Archived Jobs
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    name='sortBy'
                    value={sortBy}
                    onChange={handleChange}
                    label='Sort By'
                  >
                    {sortByOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Display Order</InputLabel>
                  <Select
                    name='displayOrder'
                    value={displayOrder}
                    onChange={handleChange}
                    label='Display Order'
                  >
                    {displayOrderOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Display Count</InputLabel>
                  <Select
                    name='displayCount'
                    value={displayCount}
                    onChange={handleChange}
                    label='Display Count'
                  >
                    {displayCountOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }} alignContent='center'>
                <Button
                  variant='contained'
                  color='brand'
                  onClick={handleSubmit}
                >
                  Search
                </Button>
              </Grid>
            </Grid>

            {rows.length ? (
              <Box
                sx={{
                  height: 'calc(100vh - 240px)',
                  width: '100%',
                  '& .MuiDataGrid-root': {
                    border: 'none',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: '1px solid rgba(224, 224, 224, 1)',
                  },
                }}
              >
                <DataGrid
                  rows={rows}
                  columns={columns}
                  getRowId={(row) => row.job_uuid}
                  pageSize={displayCount}
                  rowsPerPageOptions={[20, 50, 100]}
                  disableSelectionOnClick
                  density='comfortable'
                  loading={loading}
                  rowCount={rowCount}
                  paginationMode='server'
                  onPageChange={(page) => loadJobs(page)}
                  onPageSizeChange={(newPageSize) => {
                    setDisplayCount(newPageSize);
                    loadJobs(0);
                  }}
                  sx={{
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                  }}
                />
              </Box>
            ) : loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              renderNoJobs()
            )}
          </Box>
        </Container>
  );
}

export default ArchivedJobs;
